import { ArrowDownTrayIcon, EyeIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import type { InputType } from 'config/input.type.constants'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { openS3Document } from 'services'
import { getContractorAppPDF, updateContractorApplication } from 'services/apis'
import { Button, Checkbox, FormTableHeader, PlainTable } from 'stories/components'
import { formatTime } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { ContractorSignDialog } from '../ContractorSignDialog'

type AcknowledgementSignProps = {
  signContent: any[]
  agreeSign: boolean
  onAgreeSignUpdated: (value: boolean) => void
  canEdit: boolean
  showHistory: (key: string, headers?: Array<FormTableHeader>) => void
  signData: { fileKey?: string; override?: boolean; time?: string }
  onSignDataChange: (data: any) => void
  contractorGeneralInfo: Record<string, InputType>
  sendApplication: (copyURL: boolean) => void
  action: string
  contractorURL: any
  changeMenu: () => void
  canOverride: boolean
  status: Record<string, boolean>
  menus: Record<string, string>
  setLoading: (v: boolean) => void
}

function renderPreSignValidation(
  agreeSign: boolean,
  status: Record<string, boolean>,
  menus: Record<string, string>,
  signData: { override?: boolean },
) {
  const validations: string[] = []
  if (!agreeSign) {
    validations.push('- Contractor need to agree on acknowledgement!')
  }
  Object.keys(status).forEach((key) => {
    if (key !== 'sign' && !status[key]) {
      validations.push(`- ${menus[key]}`)
    }
  })
  if (validations.length && !signData.override) {
    return (
      <div
        className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4 text-[14px]"
        role="alert"
      >
        <div className="font-variation-settings-600">Following steps are required before Sign Application.</div>
        {validations.map((item: string, index: number) => {
          return (
            <div className="ml-2" key={index}>
              {item}
            </div>
          )
        })}
      </div>
    )
  }
  return null
}

export function AcknowledgementSign({
  signContent,
  agreeSign,
  onAgreeSignUpdated,
  canEdit,
  showHistory,
  signData,
  onSignDataChange,
  contractorGeneralInfo,
  sendApplication,
  action,
  contractorURL,
  changeMenu,
  canOverride,
  status,
  menus,
  setLoading,
}: AcknowledgementSignProps) {
  const { no: noParam } = useParams() as { no: string }
  const no = Number(noParam)
  const [showSignModal, setShowSignModal] = useState(false)

  const onChangeAgreeSign = async (key: string, value: boolean) => {
    setLoading(true)
    await updateContractorApplication(no, { [key]: value })
    onAgreeSignUpdated(value)
    setLoading(false)
  }

  const onOverride = async () => {
    setLoading(true)
    const temp = cloneDeep(signData)
    temp.override = !signData.override
    temp.time = formatTime()
    onSignDataChange(temp)
    await updateContractorApplication(no, { signData: temp })
    setLoading(false)
  }

  const applicationPDF = async () => {
    setLoading(true)
    if (signData.fileKey) openS3Document(signData.fileKey, true)
    else {
      const res = await getContractorAppPDF(no)
      if (res.success) {
        const a = document.createElement('a')
        a.href = res.content
        a.download = 'Contractor_Builder Application.pdf'
        a.click()
      }
    }
    setLoading(false)
    return true
  }

  const onOpenSignDialog = () => {
    setShowSignModal(true)
  }

  const onAfterSignFromDialog = (data: any) => {
    onSignDataChange(data)
    setTimeout(() => {
      changeMenu()
    }, 300)
  }

  const isSigned = signData.fileKey
  const isOverridden = signData.override
  const validation = renderPreSignValidation(agreeSign, status, menus, signData)
  const tableData = [
    [
      contractorGeneralInfo.entityEmail?.value || '',
      <p className="font-['freehand'] text-[24px]" key="sig-p">
        {contractorGeneralInfo.firstName?.value || ''} {contractorGeneralInfo.lastName?.value || ''}
      </p>,
      isOverridden ? 'Overridden' : isSigned ? 'Signed' : 'Unsigned',
      isSigned || isOverridden ? formatTime(signData.time) : '',

      <div
        key="pdf"
        className="flex items-center gap-2 cursor-pointer hover:underline text-shade-blue font-variation-settings-600"
        onClick={applicationPDF}
      >
        {isSigned
          ? [<span key={0}>View Signed App</span>, <EyeIcon key={1} className="w-4 h-4"></EyeIcon>]
          : [
              <span key={0}>View Unsigned App</span>,
              <ArrowDownTrayIcon key={1} className="w-4 h-4"></ArrowDownTrayIcon>,
            ]}
      </div>,
      <Checkbox
        key="override"
        id="override"
        title=""
        value={!!signData.override}
        onChange={onOverride}
        disabled={!canOverride}
        history={true}
        showHistory={() => showHistory('signData')}
      />,
    ],
  ]

  return (
    <div className="md:px-2">
      {signContent.map((item: any, index) => {
        return (
          <div className="mb-5" key={index}>
            <div className="font-semibold mb-1">{item[0]}</div>
            <div>
              <span dangerouslySetInnerHTML={{ __html: item[1] }} />
            </div>
          </div>
        )
      })}
      <div className="mb-4">
        <RenderInput
          input={{
            inputType: 'toggle',
            title: 'I agree with the above and confirm to sign.',
            error: agreeSign ? '' : 'Required!',
            value: agreeSign,
            history: true,
            disabled: !canEdit,
          }}
          Key={'agreeSign'}
          onChange={onChangeAgreeSign}
          showHistory={showHistory}
        />
      </div>
      <div className="">
        <div className="font-semibold mb-3">- Sign Application</div>
        {validation}
        <div className="overflow-x-auto">
          <div className="w-fit">
            <PlainTable
              header={['Email', 'Signature', 'Status', 'Signed Date', 'PDF', 'Override']}
              data={tableData}
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-4">
          {!contractorURL && (
            <Button onClick={() => sendApplication(false)} loading={action === 'sendApplication'}>
              <div className="flex gap-2 items-center">
                Send Signature Request
                <PaperAirplaneIcon className="w-4 h-4"></PaperAirplaneIcon>
              </div>
            </Button>
          )}
          {contractorURL &&
            (signData.fileKey ? (
              <Button onClick={changeMenu}>Go to Documents Upload</Button>
            ) : (
              <Button onClick={onOpenSignDialog} disabled={validation !== null}>
                Click Here to Sign Application
              </Button>
            ))}
        </div>
      </div>
      {showSignModal && (
        <ContractorSignDialog
          onSign={onAfterSignFromDialog}
          onClose={() => setShowSignModal(false)}
          applicationPDF={applicationPDF}
        />
      )}
    </div>
  )
}
